import { createContext, useContext, useEffect, useState } from "react";

const tokens = {
  colors: {
    background: "var(--color-background)",
    backgroundSecondary: "var(--color-background-secondary)",
    primary: "var(--color-primary)",
    primaryLight: "var(--color-primary-light)",
    secondary: "var(--color-secondary)",
    third: "var(--color-third)",
    text: "var(--color-text)",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
  },
  radius: {
    controlSm: "var(--radius-control-sm)",
    control: "var(--radius-control)",
    card: "var(--radius-card)",
    modal: "var(--radius-modal)",
    hero: "var(--radius-hero)",
    pill: "var(--radius-pill)",
  },
  shadows: {
    surfaceSm: "var(--shadow-surface-sm)",
    surface: "var(--shadow-surface)",
    overlay: "var(--shadow-overlay)",
    hero: "var(--shadow-hero)",
  },
};

const DARK_MODE_STORAGE_KEY = "darkMode";

const getStoredDarkMode = () => {
  try {
    const storedValue = window.localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (storedValue === "true") return true;
    if (storedValue === "false") return false;
  } catch {}

  return null;
};

const getInitialDarkMode = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const storedDarkMode = getStoredDarkMode();
  if (storedDarkMode !== null) return storedDarkMode;

  return (
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ??
    document.documentElement.classList.contains("dark")
  );
};

const ThemeContext = createContext({
  tokens,
  darkMode: false,
  toggleDarkMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const useTokens = () => useTheme().tokens;

export default function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const themeColor = darkMode
      ? themeColorMeta?.dataset.dark
      : themeColorMeta?.dataset.light;
    if (themeColor) {
      themeColorMeta.setAttribute("content", themeColor);
    }
  }, [darkMode]);

  useEffect(() => {
    const colorSchemeQuery = window.matchMedia?.(
      "(prefers-color-scheme: dark)",
    );
    if (!colorSchemeQuery) return undefined;

    const followSystemPreference = (event) => {
      if (getStoredDarkMode() === null) {
        setDarkMode(event.matches);
      }
    };

    colorSchemeQuery.addEventListener?.("change", followSystemPreference);
    return () =>
      colorSchemeQuery.removeEventListener?.("change", followSystemPreference);
  }, []);

  const toggleDarkMode = () =>
    setDarkMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(DARK_MODE_STORAGE_KEY, String(next));
      } catch {}
      return next;
    });

  const value = {
    tokens,
    darkMode,
    toggleDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
